import {Component, Inject, Input, OnInit } from '@angular/core';
import * as _ from 'lodash';

import { ConstructionObjectNamePipe } from '@app/common/pipes/construction-object-name.pipe';
import { GeometricPlanRequestService } from '@app/ps/services/geometric-plan-request.service';
import { ParcelNumberPipe } from '@app/common/pipes/parcel-number.pipe';
import { GeometricPlanRequestModel } from '@app/ps/models/geometric-plan-request.model';
import { GeometricPlanRequestEditComponent } from '@app/ps/geometric-plans/components/geometric-plan-request-edit/geometric-plan-request-edit.component';
import { UploadFileExtended } from '@app/common/components/fileupload/fileupload.component';
import { AreaModel } from '@app/common/models/area.model';
import { GeometricPlanRequestStatusEnum } from '@app/ps/enums/geometric-plan-request-status.enum';
import { StateService } from '@uirouter/angular';
import { GeometricPlanRequestTypeEnum } from '@app/ps/enums/geometric-plan-request-type.enum';
import { ParcelModel } from '@app/common/models/parcel.model';
import { Restangular } from 'ngx-restangular';
import { HelpService } from '@app/common/services/help.service';
import { ParcelUtils } from '@app/common/utils/parcel.utils';
import { ProjectSubjectNamePipe } from '@app/ps/pipes/project-subject-name.pipe';
import { DialogService } from '@app/common/services/dialog.service';
import { SupplierEditComponent } from '@app/ps/components/supplier-edit/supplier-edit.component';
import { ProjectSubjectModel } from '@app/ps/models/project-subject.model';
import { APP_BRAND } from '@app/common/services/config.service';

@Component({
  selector: 'geometric-plan-request-import',
  templateUrl: './geometric-plan-request-import.component.html',
  styleUrls: ['./geometric-plan-request-import.component.scss'],
})
export class GeometricPlanRequestImportComponent implements OnInit {

  @Input() geometricPlanRequest: GeometricPlanRequestModel;
  step;
  data = [];
  parcels: ParcelModel[];
  typeEnum = GeometricPlanRequestTypeEnum;
  currentSum = 0;
  budoucSum = 0;
  private srcData;

  area: AreaModel;
  constructionObjectFilter = {
    validity: { values: ['valid'] },
    areaId: { values: [] },
  };

  static readonly MSG_GP_CSV_FILES_DEFAULT = 'Vložte soubor přetažením nebo výběrem. Je možné vložit jeden soubor CSV.';
  static readonly MSG_GP_CSV_FILES_EXTENSION = 'Soubor musí být ve formátu CSV.';

  gpFiles: UploadFileExtended[] = [];
  gpVfkFiles: UploadFileExtended[] = [];
  gpCsvFiles: UploadFileExtended[] = [];
  gpFilesValid = false;
  gpVfkFilesValid = false;
  gpCsvFilesValid = false;
  hasFile = false;
  hasVfkFile = false;

  gpFilesMsg = GeometricPlanRequestEditComponent.MSG_GP_FILES_DEFAULT;
  gpVfkFilesMsg = GeometricPlanRequestEditComponent.MSG_GP_VFK_FILES_DEFAULT;
  gpCsvFilesMsg = GeometricPlanRequestImportComponent.MSG_GP_CSV_FILES_DEFAULT;

  helpIds = HelpService.HELP_IDS;
  budoucParOptions: any[];

  constructor(
    private restangular: Restangular,
    public constructionObjectNamePipe: ConstructionObjectNamePipe,
    public parcelNumberPipe: ParcelNumberPipe,
    public requestService: GeometricPlanRequestService,
    private stateService: StateService,
    public projectSubjectNamePipe: ProjectSubjectNamePipe,
    private dialogService: DialogService,
    @Inject(APP_BRAND) private APP_BRAND: any,
  ) {
    this.onSave = this.onSave.bind(this);
    this.onFirstStep = this.onFirstStep.bind(this);
    this.onSecondStep = this.onSecondStep.bind(this);
  }

  async ngOnInit() {
    this.area = this.geometricPlanRequest.areas[0];
    await this.checkAttachments();
    this.checkStep();
  }

  onCadastreChanged() {
    this.constructionObjectFilter.areaId.values = [this.area.id];
  }

  onGpFilesChange(files: UploadFileExtended[]) {
    this.gpFiles = files;

    const extensionValid = files.every(f => f.extension && f.extension.toLowerCase() === 'pdf');

    if (this.gpFiles.length === 0) {
      this.gpFilesMsg = GeometricPlanRequestEditComponent.MSG_GP_FILES_DEFAULT;
    } else if (!extensionValid) {
      this.gpFilesMsg = GeometricPlanRequestEditComponent.MSG_GP_FILES_EXTENSION;
    } else if (this.gpFiles.length > 1) {
      this.gpFilesMsg = GeometricPlanRequestEditComponent.MSG_FILES_QUANTITY;
    } else {
      this.gpFilesMsg = '';
    }

    this.gpFilesValid = (extensionValid && this.gpFiles.length === 1);
  }

  onGpVfkFilesChange(files: UploadFileExtended[]) {
    this.gpVfkFiles = files;

    const extensionValid = files.every(f => f.extension && f.extension.toLowerCase() === 'vfk');

    if (this.gpVfkFiles.length === 0) {
      this.gpVfkFilesMsg = GeometricPlanRequestEditComponent.MSG_GP_VFK_FILES_DEFAULT;
    } else if (!extensionValid) {
      this.gpVfkFilesMsg = GeometricPlanRequestEditComponent.MSG_GP_VFK_FILES_EXTENSION;
    } else if (this.gpVfkFiles.length > 1) {
      this.gpVfkFilesMsg = GeometricPlanRequestEditComponent.MSG_FILES_QUANTITY;
    } else {
      this.gpVfkFilesMsg = '';
    }

    this.gpVfkFilesValid = (extensionValid && this.gpVfkFiles.length === 1);
  }

  onGpCsvFilesChange(files: UploadFileExtended[]) {
    this.gpCsvFiles = files;

    const extensionValid = files.every(f => f.extension && f.extension.toLowerCase() === 'csv');

    if (this.gpCsvFiles.length === 0) {
      this.gpCsvFilesMsg = GeometricPlanRequestImportComponent.MSG_GP_CSV_FILES_DEFAULT;
    } else if (!extensionValid) {
      this.gpCsvFilesMsg = GeometricPlanRequestImportComponent.MSG_GP_CSV_FILES_EXTENSION;
    } else if (this.gpCsvFiles.length > 1) {
      this.gpCsvFilesMsg = GeometricPlanRequestEditComponent.MSG_FILES_QUANTITY;
    } else {
      this.gpCsvFilesMsg = '';
    }

    this.gpCsvFilesValid = (extensionValid && this.gpCsvFiles.length === 1);
  }

  isValidForSecondStep(): boolean {
    return !!(
      this.geometricPlanRequest.name
      && this.geometricPlanRequest.supplier
      && this.geometricPlanRequest.verificationDate
      && this.geometricPlanRequest.approvedDate
      && this.geometricPlanRequest.pgpNumber
      && this.area
      && (this.geometricPlanRequest.type !== GeometricPlanRequestTypeEnum.EASEMENT_EXTENT || this.geometricPlanRequest.constructionObjects.length)
      && ((this.gpFiles.length === 0 && this.hasFile) || this.gpFiles.length > 0 && this.gpFilesValid)
      && ((this.gpVfkFiles.length === 0 && this.hasVfkFile) || this.gpVfkFiles.length > 0 && this.gpVfkFilesValid)
    );
  }

  checkStep(): Promise<any> {
    if (this.geometricPlanRequest.vfkImportedId) {
      return this.processGp()
        .then(() => {
          this.prepareData();
          this.step = 2;
        });
    } else {
      this.step = 1;
    }
  }

  onFirstStep() {
    this.gpFiles = [];
    this.gpVfkFiles = [];
    this.gpCsvFiles = [];
    this.gpFilesValid = false;
    this.gpVfkFilesValid = false;
    this.gpCsvFilesValid = false;
    this.hasFile = false;
    this.hasVfkFile = false;

    this.gpFilesMsg = GeometricPlanRequestEditComponent.MSG_GP_FILES_DEFAULT;
    this.gpVfkFilesMsg = GeometricPlanRequestEditComponent.MSG_GP_VFK_FILES_DEFAULT;
    this.gpCsvFilesMsg = GeometricPlanRequestImportComponent.MSG_GP_CSV_FILES_DEFAULT;

    this.checkAttachments();
    this.step = 1;
  }

  onSecondStep(): Promise<any> {
    this.geometricPlanRequest.areas = this.area ? [this.area] : [];
    return this.requestService.update(this.geometricPlanRequest, [], this.gpFiles, this.gpVfkFiles, [], this.gpCsvFiles)
      .then(gpr => {
        this.geometricPlanRequest = gpr;
      }).then(() => {
        if (this.geometricPlanRequest.type != GeometricPlanRequestTypeEnum.EASEMENT_EXTENT) {
          return this.processGp();
        }
      }).then(() => {
        if (this.geometricPlanRequest.type == GeometricPlanRequestTypeEnum.EASEMENT_EXTENT) {
           return this.stateService.go('symap.project.constructionObjects.newEasement', { geometricPlanRequestId: this.geometricPlanRequest.id });
        }
        this.prepareData();
        this.step = 2;
      });
  }

  onSave() {
    // put changes into srcData
    for (const d of this.data) {
      for (const b of d.budouc) {
        const srcB = this.srcData.budouc.find(bb => b.id === bb.id);
        srcB.toImport = b.toImport;
        srcB.parcis = b.parcis;
        srcB.vymera = b.vymera;
        srcB.druhpozNazev = b.druhpozNazev;
        srcB.zpusobVyuziti = b.zpusobVyuziti;
        srcB.cislKod = b.cislKod;
        srcB.katuzeKod = b.katuzeKod;
        srcB.drupozKod = b.drupozKod;
        srcB.zpvypaKod = b.zpvypaKod;
        srcB.lv = b.lv;

        for (const p of b.pepv) {
          const srcP = this.srcData.pepv.find(pp => p.id === pp.id);
          srcP.vymeraDilu = p.vymeraDilu;
          srcP.oznaceniDilu = p.oznaceniDilu;
          srcP.parcisBudouci = p.parcisBudouci;
          srcP.cislKod = p.cislKod;
        }
      }
    }

    return this.restangular.one(`vfks/data/${this.geometricPlanRequest.vfkImportedId}`)
      .customPUT({ knBudouc: this.srcData.budouc, knBudoucPepv: this.srcData.pepv }, undefined, { updateCollections: ['knBudouc', 'knBudoucPepv'] })
      .toPromise()
      .then(() => {
        return this.restangular.one(`geometric-plan-requests/${this.geometricPlanRequest.id}/import-vfk-perform`).get().toPromise();
      })
      .then(() => {
        this.geometricPlanRequest.status = GeometricPlanRequestStatusEnum.IMPORTED_AUTO;
        return this.requestService.update(this.geometricPlanRequest).then(gpr => {
          this.stateService.go('symap.project.geometricPlanRequests.detail', { id: this.geometricPlanRequest.id, tab: 'parcels' });
        });
      });
  }

  private checkAttachments() {
    if (this.APP_BRAND.NAME === 'RSD') {
      return this.restangular.all('documents')
        .customPOST({ filter: { filters: { geometricPlanRequestId: this.geometricPlanRequest.id, cancelled: false, loadCollections: ['components'] }}})
        .toPromise()
        .then((documents => {
          documents.forEach(d => {
            if (d.components.filter(c => c.attachmentType === 'GEOMETRIC_PLAN').length > 0) {
              this.hasFile = true;
            }
            if (d.components.filter(c => c.attachmentType === 'GEOMETRIC_PLAN_VFK').length > 0) {
              this.hasVfkFile = true;
            }
          });
        }));
    } else {
      return this.restangular
        .all('attachments')
        .customPOST({ filter: { filters: { geometricPlanRequestId: this.geometricPlanRequest.id, cancelled: false }}})
        .toPromise()
        .then((data => {
          this.hasFile = data.filter(d => d.type === 'GEOMETRIC_PLAN').length > 0;
          this.hasVfkFile = data.filter(d => d.type === 'GEOMETRIC_PLAN_VFK').length > 0;
        }));
    }
  }

  private processGp(): Promise<any> {
    const initPromise = this.geometricPlanRequest.vfkImportedId
      ? Promise.resolve()
      : this.restangular.one(`geometric-plan-requests/${this.geometricPlanRequest.id}/import-vfk-prepare`).get().toPromise()
        .then(gpr => { this.geometricPlanRequest.vfkImportedId = gpr.vfkImportedId; });

    return initPromise
      .then(() => {
        return this.restangular.one(`vfks/data/${this.geometricPlanRequest.vfkImportedId}`).get({ loadCollections: ['knBudouc', 'knBudoucPepv', 'par'] }).toPromise();
      })
      .then((vfk) => {
        this.srcData = {
          pepv: vfk.knBudoucPepv,
          budouc: vfk.knBudouc,
        };

        /* Výběr z budoucích parcel, které nejsou již zavedené v akci */
        this.budoucParOptions = vfk.par
          .map(p => {
            p.name = p.kmenoveCisloPar + (p.poddeleniCislaPar ? '/' + p.poddeleniCislaPar : '');
            return p;
          })
          .filter(p => (p.stavDat == 1 || (p.stavDat == 0 && p.priznakKontextu == 3))
            && !vfk.knBudouc.some(b => b.katuzeKod == p.katuzeKod && b.parcis == p.name && b.cislKod == p.druhCislovaniPar && (b.imported || b.vstupExists))
          );

        const idpars = this.srcData.pepv.map(p => p.idpar);

        if (!idpars.length) {
          return Promise.resolve([]);
        }

        return this.restangular.all('parcels').customPOST({ filter: { filters: { idpar: idpars, validity: [ 'valid' ] }}}).toPromise();
      })
      .then((parcels) => {
        this.parcels = parcels;
      });
  }

  private prepareData() {
    this.data = [];
    const budouc = this.srcData.budouc.map(b => {
      const pepvFiltered = this.srcData.pepv
         .filter((pp: any) => b.id === pp.importVfkKnBudoucId)
         .sort((i1, i2) => {
           return i1.parcis < i2.parcis ? -1 : 1;
       });
      const dily = pepvFiltered.filter(p => p.toImport).length > 1;
      pepvFiltered.forEach(p => { p.toImport = !b.vstupExists && !dily });
      if (!pepvFiltered.length) {
        b.toImport = false;
      } else {
        b.toImport = !b.vstupExists;
      }
      return {
        ...b,
        pepv : pepvFiltered,
        dily : false,
        // přiřadit prioritně k parcele stávající se stejným číslem
        parcelCurrent : pepvFiltered.find(p => p.parcis == b.parcis) || pepvFiltered[0],
        validPepv : !dily,
      }
    })
    /* Polygony, které nemají žádné pepv, nebo nejsou ve vfk a vznikly v okolí
    buď jako uzavřený polygon parcelami nebo na hranici ze sousedních parcel */
    .filter(b => b.parcelCurrent);

    for (const p of this.srcData.pepv) {
      let d = this.data.find(dd => dd.parcel.idpar === p.idpar);
      if (!d) {
        d = {};
        d.current = {...p};
        d.parcel = this.parcels.find(pp => pp.idpar === p.idpar);
        d.budouc = budouc
          .filter(b => b.parcelCurrent.idpar == p.idpar)
          .sort((i1, i2) => {
            return i1.parcis == p.parcis || (i2.parcis != p.parcis && i1.parcis < i2.parcis) ? -1 : 1;
          });
        d.warnings = null;
        this.data.push(d);

        // zkontrolovat existenci podlomení
        this.checkErrorPodlomeni(d);
        // zkontrolovat soulad kmenu
        d.budouc.forEach(b => {
          this.checkErrorKmen(d, b);
        });
      }
    }

    this.data = _.sortBy(this.data, d => ParcelUtils.getParcelSortNumber(d.parcel.parcisKmen, d.parcel.parcisPod, d.parcel.parcisDil));

    // control sum
    this.currentSum = this.data.reduce((t, d) => t + parseInt(d.parcel.vymera), 0);
    this.countBudoucSum();
  }

  private countBudoucSum() {
    this.budoucSum = this.data.reduce((t, d) => {
      return t + d.budouc.reduce((tt, b) => {
          if (!b.vymera) {
            return tt;
          }
          return tt + parseInt(b.vymera);
        },
        0
      );
    }, 0);
  }

  private checkErrorPodlomeni(d: any) {
    if (d.current.parcis.includes('/') && !d.budouc.some(b => b.parcis === d.current.parcis)) {
      d.warning = "Neexistuje parcela se stejným podlomením";
    } else {
      d.warning = null;
    }
  }

  private checkErrorKmen(d: any, b: any) {
    const kmen = d.current.parcis.split('/')[0];
    if (b.parcis && kmen != b.parcis.split('/')[0]) {
      b.warning = "Nesouhlasí kmenové číslo parcely";
    } else {
      b.warning = null;
    }
  }

  public onPepvToImportClick(pepv, budouc) {
    if (!budouc.toImport || budouc.vstupExists) {
      return;
    }
    pepv.toImport = !pepv.toImport;
    budouc.validPepv = !!budouc.pepv.filter(p => p.toImport).length;
    budouc.dily = budouc.validPepv && budouc.pepv.filter(p => p.toImport).length > 1;
  }

  public onBudoucToImportClick(budouc) {
    budouc.toImport = (budouc.vstupExists || budouc.imported ? budouc.toImport : !budouc.toImport);
    if (!budouc.toImport) {
      budouc.pepv.forEach(p => { p.toImport = false });
    }
  }

  public isValid() : boolean {
    for (const d of this.data) {
      if (d.budouc.some(b => b.toImport && !b.validPepv)) {
        return false;
      }
    }
    return true;
  }

  public onParSelected(budouc, event) {
    budouc.parcis = event.newValue.name;
    budouc.vymera = event.newValue.vymeraParcely;
    budouc.druhpozNazev = event.newValue.druhpozNazev;
    budouc.zpusobVyuziti = event.newValue.zpusobVyuziti;
    budouc.cislKod = event.newValue.druhCislovaniPar;
    budouc.katuzeKod = event.newValue.katuzeKod;
    budouc.drupozKod = event.newValue.drupozKod;
    budouc.zpvypaKod = event.newValue.zpvypaKod;
    budouc.lv = event.newValue.lv;
    budouc.pepv.forEach(p => {
      if (p.importVfkKnBudoucId == budouc.id) {
        p.parcisBudouci = event.newValue.name;
        p.cislKod = event.newValue.druhCislovaniPar;
      }
    });
    this.countBudoucSum();
    this.data.forEach(d => {
      d.budouc.forEach(b => {
        if (b == budouc) {
          this.checkErrorPodlomeni(d);
          this.checkErrorKmen(d, b);
        } else if (b.parcis == event.newValue.name) {
          b.parcis = null;
          b.vymera = null;
          b.druhpozNazev = null;
          b.zpusobVyuziti = null;
          b.cislKod = null;
          b.katuzeKod = null;
          b.drupozKod = null;
          b.zpvypaKod = null;
          b.lv = null;
          this.checkErrorPodlomeni(d);
          this.checkErrorKmen(d, b);
          b.pepv.forEach(p => {
            if (p.importVfkKnBudoucId == b.id) {
              p.parcisBudouci = null;
              p.cislKod = null;
            }
          });
        }
      });
    });
  }

  comparePar(selected: any, item: any): boolean {
    return selected.name == item.name;
  }

  onUpdateSupplier() {
    const dialog = this.dialogService.open(SupplierEditComponent, {
      data: { supplierId: this.geometricPlanRequest.supplier.id }
    });

    const sub = dialog.afterClosed.subscribe((res: ProjectSubjectModel) => {
      if (res) {
        _.extend(this.geometricPlanRequest.supplier, res);
      }
      sub.unsubscribe();
    });
  }

  onCreateSupplier() {
    const dialog = this.dialogService.open(SupplierEditComponent, { data: {} });
    const sub = dialog.afterClosed.subscribe((res: ProjectSubjectModel) => {
      if (res) {
        this.geometricPlanRequest.supplier = res;
      }
      sub.unsubscribe();
    });
  }
}
